import flatten from 'lodash/flatten';
import urlJoin from '@framework/url/helpers/urlJoin';
import base from '@framework/url/baseUrl';
import { buildUrl } from '@framework/url/helpers/buildUrl';
import isServer from '@framework/router/Next/isServer';
import config from '@framework/config';
import usersConfig from '@users/config';
import getUnixTimestamp from '@framework/date-helpers/getUnixTimestamp';
import shouldUseInternalNetwork from '@framework/router/Next/shouldUseInternalNetwork';
import { base64Encode } from '@framework/data-helpers/base64/base64Encode';
import { jsonStringify } from '@framework/data-helpers/json/jsonStringify';

export default {
  /**
   * Get base users url
   */
  getUrl(uris: string | string[] = []) {
    if (!Array.isArray(uris)) {
      uris = [uris];
    }
    uris = flatten(uris).filter((uri) => !!uri);
    return urlJoin(`${ config.protocol }//${ config.usersDomain }`, ...uris);
  },

  /**
   * Get base helpers url
   *
   * @param {Array|String} uris
   *
   * @return {String}
   */
  getApiUrl(uris: string | string[] = []): string {
    return base.getApiUrl('users/api', uris);
  },

  /**
   * Get base rpc helpers url
   *
   * @return {String}
   */
  getRpcUrl(): string {
    if (shouldUseInternalNetwork()) {
      return this.getInternalRpcUrl();
    }

    if (!isServer()) {
      return this.getProxyApiRpcPath();
    }

    return base.getApiUrl('/users/rpc');
  },

  getInternalRpcUrl(): string {
    return `${ usersConfig.api.internal }/rpc`;
  },

  getExternalUrl(): string {
    return `${ usersConfig.api.external }`;
  },
  /**
   * Gets proxy path.
   *
   * @return {String}
   */
  getProxyPath() {
    return `${ window.location.protocol }//${ window.location.hostname }/proxy/users`;
  },

  /**
   * Gets proxy api path.
   *
   * @return {String}
   */
  getProxyApiPath() {
    return `${ this.getProxyPath() }/api`;
  },

  /**
   * Gets proxy api rpc path.
   *
   * @return {String}
   */
  getProxyApiRpcPath() {
    return `${ this.getProxyPath() }/rpc`;
  },

  /**
   * Gets proxy api logout path.
   *
   * @return {String}
   */
  getProxyApiLogoutPath() {
    return `${ this.getProxyApiPath() }/session/logout`;
  },

  getSessionPropagateUrl(callbackUrl: string, SAMLRequest?: string, RelayState?: string) {
    // @ts-ignore
    const query = new URLSearchParams({
      callbackUrl,
      t: getUnixTimestamp(),
      ...(SAMLRequest && { SAMLRequest }),
      ...(RelayState && { RelayState }),
    });

    const newUrl = new URL(`?${ query.toString() }`, this.getApiUrl('session/propagate/'));
    return newUrl.href;
  },

  /**
   * Get logout url
   *
   * @param {String} callbackUrl
   *
   * @return {String}
   */
  getLogoutUrl(callbackUrl: string) {
    // @ts-ignore
    const query = new URLSearchParams({ callbackUrl, t: getUnixTimestamp() });
    const newUrl = new URL(`?${ query.toString() }`, this.getApiUrl('/session/logout/'));
    return newUrl.href;
  },

  /**
   * Get user profile link with correct callbackUrl and tenant
   *
   * @param {String} tenant
   *
   * @return {String}
   */
  getProfileUrl(tenant: string) {
// @ts-ignore TODO: FIN-32798 strictNullChecks
    const query = new URLSearchParams({ tenant, callbackUrl: !isServer() && window.location.href });
    const newUrl = new URL(`?${ query.toString() }`, this.getUrl('/profile/'));
    return newUrl.href;
  },

  /**
   * Get check password link url for two factor auth with correct callbackUrl and tenant
   *
   * @param {String} tenant
   *
   * @return {String}
   */
  getCheckPasswordUrl(tenant: string) {
// @ts-ignore TODO: FIN-32798 strictNullChecks
    const query = new URLSearchParams({ tenant, callbackUrl: !isServer() && window.location.href });
    const newUrl = new URL(`?${ query.toString() }`, this.getUrl('/two_factor_authentication/check_password/'));
    return newUrl.href;
  },

  /**
   * Get login url
   *
   * @param {String} callbackUrl
   * @param {String} tenant
   * @param {Object} [customParams]
   *
   * @return {String}
   */
  getLoginUrl(callbackUrl?: string, tenant?: string, customParams?: { [key: string]: any }) {
    const params = [
      callbackUrl ? `callbackUrl=${ encodeURIComponent(callbackUrl) }` : '',
      tenant ? `tenant=${ tenant }` : '',
      customParams ? `customParams=${ base64Encode(jsonStringify(customParams)) }` : '',
    ]
      .filter((param) => !!param)
      .join('&');

    return this.getUrl(`/login${ params ? `?${ params }` : '' }`);
  },
  /**
   * Get register url
   *
   * @param {String} callbackUrl
   * @param {String} tenant
   *
   * @return {String}
   */
  getRegisterUrl(callbackUrl?: string, tenant?: string) {
    const params = [
      callbackUrl ? `callbackUrl=${ encodeURIComponent(callbackUrl) }` : '',
      tenant ? `tenant=${ tenant }` : '',
    ]
      .filter((param) => !!param)
      .join('&');

    return this.getUrl(`/register${ params ? `?${ params }` : '' }`);
  },

  /**
   * @param {String} email
   * @param {String|null} callbackUrl
   * @param {String|null} SAMLRequest
   * @param {String|null} RelayState
   * @returns {String}
   */
  getSsoLoginUrl(
    email: string,
    callbackUrl: string,
    SAMLRequest: string | null = null,
    RelayState: string | null = null,
  ) {
    const loginPath = '/sso/login/';
    const query = new URLSearchParams({
      ...(callbackUrl && { callbackUrl }),
// @ts-ignore TODO: FIN-32798 strictNullChecks
      ...(SAMLRequest && { SAMLRequest }),
// @ts-ignore TODO: FIN-32798 strictNullChecks
      ...(RelayState && { RelayState }),
    });
    const newUrl = new URL(`?${ query.toString() }&email=${ encodeURIComponent(email) }`, this.getApiUrl(loginPath));
    return newUrl.href;
  },

  /**
   * @param {String} ssoProvider
   * @param {String|null} callbackUrl
   * @returns {String}
   */
  getSsoVerifyLoginUrl(ssoProvider: string, callbackUrl: string) {
    const loginPath = `/sso/${ ssoProvider }/login/verify/`;
    const query = new URLSearchParams({ ...(callbackUrl && { callbackUrl }) });
    const newUrl = new URL(`?${ query.toString() }`, this.getApiUrl(loginPath));
    return newUrl.href;
  },

  /**
   * @param {Array||String} uris
   * @return {String}
   */
  getBaseUrl(...uris: string[]) {
    return buildUrl(config.protocol, config.host, ...uris);
  },
};
